import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n/LanguageContext";
import { useUser } from "../context/UserContext";
import reportingAPI from "../services/ReportingAPI";
import { formatDateOnly } from "../utils/DateTimeUtil";
import "../css/Common.css";
import "./css/ShieldReports.css";
import LoadingSpinner from "../common/LoadingSpinner";
import ReadOnlyBanner from "../common/ReadOnlyBanner";

/**
 * Shield Reports Page
 * Lists all user reports with filtering and pagination
 */
function ShieldReports({ setHeroContent }) {
  const { loggedInUser } = useUser();
  const { t } = useTranslation();
  const [fromSetupJourney] = useState(
    () => sessionStorage.getItem("fromSetupJourney") === "true",
  );

  // Clear the flag so it doesn't persist on page refresh
  useEffect(() => {
    if (fromSetupJourney) {
      sessionStorage.removeItem("fromSetupJourney");
    }
  }, [fromSetupJourney]);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    hasMore: false,
  });
  const [eligibility, setEligibility] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (loggedInUser?.uuid) {
      fetchReports();
      checkEligibility();
    }
  }, [loggedInUser]);

  const fetchReports = async (page = 0) => {
    try {
      setLoading(true);
      setError(null);

      const response = await reportingAPI.listReports({ page, size: 10 });

      setReports(response.reports || []);
      setPagination({
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        totalItems: response.totalItems,
        hasMore: response.hasMore,
      });
    } catch (err) {
      console.error("[ShieldReports] Error fetching reports:", err);
      setError("Failed to load reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const checkEligibility = async () => {
    try {
      const response = await reportingAPI.checkEligibility();
      setEligibility(response);
    } catch (err) {
      console.error("[ShieldReports] Error checking eligibility:", err);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      setError(null);

      const response = await reportingAPI.generatePersonalReport({
        periodType: "WEEKLY",
        sendEmail: true,
      });

      if (response.success) {
        // Check if this was an existing report or a newly generated one
        const isExistingReport = response.message?.includes("already exists");

        if (isExistingReport) {
          // Show toast message instead of navigating
          toast(
            "A report already exists for last week. View it from the list below.",
            {
              icon: "ℹ️",
              duration: 5000,
              style: {
                background: "#eff6ff",
                color: "#1e40af",
                border: "1px solid #bfdbfe",
              },
            },
          );
          // Just refresh the list, don't navigate
          await fetchReports();
        } else {
          // New report was generated - show success and navigate
          toast.success(t("shieldReports.toast.reportGenerated"), {
            duration: 3000,
          });
          await fetchReports();
          setHeroContent(`Shield Report Detail:${response.reportUuid}`);
        }
      } else {
        // Handle error response from backend (e.g., missing criteria)
        toast.error(response.message || "Unable to generate report.", {
          duration: 5000,
        });
      }
    } catch (err) {
      console.error("[ShieldReports] Error generating report:", err);
      // Extract error message from the error object
      const errorMessage =
        err.message || "Failed to generate report. Please try again.";
      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleViewReport = (reportUuid) => {
    setHeroContent(`Shield Report Detail:${reportUuid}`);
  };

  const handleDownloadPdf = async (reportUuid, e) => {
    e.stopPropagation();
    try {
      await reportingAPI.downloadPdfToDevice(reportUuid);
    } catch (err) {
      console.error("[ShieldReports] Error downloading PDF:", err);
    }
  };

  const handlePageChange = (newPage) => {
    fetchReports(newPage);
  };

  // Loading state
  if (loading && reports.length === 0) {
    return (
      <>
        {/* Header */}
        <div className="common-header">
          <h1 className="main-title">Shield Reports</h1>
          <p className="common-subtitle">
            Track your security progress over time
          </p>
        </div>
        <div className="common-container shield-reports-content">
          <LoadingSpinner message="Loading your reports..." />
        </div>
      </>
    );
  }

  return (
    <>
      <ReadOnlyBanner
        onReturn={() => setHeroContent && setHeroContent("Member User Info")}
      />
      {/* Header */}
      <div className="common-header">
        <h1 className="main-title">Shield Reports</h1>
        <p className="common-subtitle">
          Track your security progress over time
        </p>
      </div>

      {/* Content Container */}
      <div className="common-container shield-reports-content">
        {/* Generate Report Button */}
        {eligibility?.eligible && (
          <div className="shield-reports-actions">
            <button
              className="btn btn-primary generate-report-btn"
              onClick={handleGenerateReport}
              disabled={generating}
            >
              {generating ? "Generating..." : "Generate New Report"}
            </button>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="shield-reports-error">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {/* Not Eligible Banner */}
        {eligibility && !eligibility.eligible && (
          <div className="shield-reports-eligibility">
            <div className="eligibility-icon">🔒</div>
            <div className="eligibility-content">
              <h4>Reports Not Available Yet</h4>
              <p>{eligibility.reason}</p>
              <p className="eligibility-recommendation">
                {eligibility.recommendation}
              </p>
            </div>
          </div>
        )}

        {/* Reports List */}
        {reports.length > 0 ? (
          <>
            <div className="shield-reports-list">
              {/* Table Header */}
              <div className="report-row report-header-row">
                <span className="report-col col-type">Type</span>
                <span className="report-col col-period">Period</span>
                <span className="report-col col-score">Score</span>
                <span className="report-col col-date">Created</span>
                <span className="report-col col-status">Status</span>
                <span className="report-col col-actions">Actions</span>
              </div>
              {/* Report Rows */}
              {reports.map((report) => (
                <div
                  key={report.reportUuid}
                  className="report-row"
                  onClick={() => handleViewReport(report.reportUuid)}
                >
                  <span className="report-col col-type">
                    <span className="report-type-badge">
                      {report.reportType === "PERSONAL_SHIELD"
                        ? "Personal"
                        : report.reportType}
                    </span>
                  </span>
                  <span className="report-col col-period">
                    {reportingAPI.formatPeriodLabel(
                      report.periodType,
                      report.periodStart,
                      report.periodEnd,
                    )}
                  </span>
                  <span
                    className="report-col col-score"
                    style={{
                      color: reportingAPI.getScoreColor(report.shieldScore),
                    }}
                  >
                    <strong>{report.shieldScore}</strong>
                  </span>
                  <span className="report-col col-date">
                    {formatDateOnly(report.createdAt)}
                  </span>
                  <span className="report-col col-status">
                    {report.emailDelivered && (
                      <span className="status-badge delivered">✉️ Emailed</span>
                    )}
                    {report.downloadCount > 0 && (
                      <span className="status-badge">
                        📥 {report.downloadCount}
                      </span>
                    )}
                  </span>
                  <span className="report-col col-actions">
                    <button
                      className="action-btn view"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewReport(report.reportUuid);
                      }}
                    >
                      View
                    </button>
                    <button
                      className="action-btn download"
                      onClick={(e) => handleDownloadPdf(report.reportUuid, e)}
                    >
                      PDF
                    </button>
                  </span>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="shield-reports-pagination">
                <button
                  className="pagination-btn"
                  disabled={pagination.currentPage === 0}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.currentPage + 1} of {pagination.totalPages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={!pagination.hasMore}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          eligibility?.eligible && (
            <div className="shield-reports-empty">
              <div className="empty-icon">📊</div>
              <h3>No Reports Yet</h3>
              <p>
                Generate your first Shield Report to see your security progress.
              </p>
              <button
                className="generate-report-btn"
                onClick={handleGenerateReport}
                disabled={generating}
              >
                {generating ? "Generating..." : "Generate Your First Report"}
              </button>
            </div>
          )
        )}
      </div>

      {/* Back to Setup Journey link — shown when navigated from AccountSetupJourney */}
      {fromSetupJourney && setHeroContent && (
        <div className="setup-journey-return">
          <button
            className="setup-journey-return-link"
            onClick={() => {
              sessionStorage.setItem("expandSetupJourney", "true");
              setHeroContent("Dashboard");
            }}
          >
            <span className="return-arrow">←</span>
            Continue Setting Up Your Account
          </button>
        </div>
      )}
    </>
  );
}

export default ShieldReports;
